import { Immutable } from "immer";
import React from "react";
import {
    Gallery,
    IGalleryImage,
} from "tsi-common-react/src/components/Gallery/index";

import type { CarouselModule as Value } from "@reactivated";

type Props = Immutable<{
    value: Value;
}>;

type CarouselItem = NonNullable<Value["carousel"]>[number];

/**
 * React port of the tempurpedic ``CarouselModule`` block (legacy template
 * ``cms/components/pdp-carousel.html``). The legacy template serialized
 * gallery images to a JSON blob (``build_pdp_gallery_ssr_context`` ->
 * ``images_data_json``) and handed it to the ``PdpGalleryTemplate`` reactivated
 * component, which JSON.parsed it back out and rendered the shared
 * ``<Gallery images alternateArrows>``. The consolidated PDP body assembly
 * (#33589) reaches this component directly, so it builds the
 * ``IGalleryImage[]`` from the typed Wagtail renditions on ``block.value``
 * (the TP ``CarouselBlock.image`` now declares the matching
 * ``width-200`` + ``original`` ``extra_client_data_renditions``), producing
 * markup equivalent to the legacy SSR path. (sealy / toppers precedent.)
 *
 * The legacy template wrapped the include in ``{% if value.carousel %}`` and
 * added no container markup, so this component returns ``null`` when there are
 * no usable images and renders ``<Gallery>`` bare otherwise.
 *
 * ``alternateArrows`` is ``true``: tempurpedic's ``CarouselModule`` keeps the
 * ``PdpGalleryCarouselMixin`` default of ``True`` (it does NOT set
 * ``alternate_arrows = False`` the way sealy / toppers do), and the legacy
 * template passed that value straight through to ``PdpGalleryTemplate`` ->
 * ``Gallery``. (Do NOT "fix" this to ``false`` to match sealy / toppers --
 * tempurpedic deliberately diverges, like pillows.)
 */
const buildGalleryImage = (
    item: Immutable<CarouselItem>,
): IGalleryImage | null => {
    const image = item.image;
    if (!image) {
        return null;
    }
    const originalURL = image.renditions["original"]?.url ?? image.url;
    const thumbnailURL = image.renditions["width-200"]?.url ?? image.url;
    // Legacy SSR used the rendition's ``.alt``, which equals Wagtail's
    // ``default_alt_text`` (description-or-title). Prefer that for parity.
    const alt = image.default_alt_text || image.title || "";
    return {
        originalURL,
        originalAlt: alt,
        thumbnailURL,
        thumbnailAlt: alt,
        caption: item.caption ?? "",
    };
};

export const CarouselModule = (props: Props): React.JSX.Element | null => {
    const carousel = props.value.carousel ?? [];
    const images = carousel
        .map((item) => buildGalleryImage(item))
        .filter((img): img is IGalleryImage => img !== null);

    if (images.length === 0) {
        return null;
    }

    return <Gallery images={images} alternateArrows={true} />;
};
